// Pure helpers for the uploaded People lists (PeoplePanel.web.tsx and the
// Assign form, DefensePlanAssignForm.web.tsx).
import type { PeopleList, Person, PlanPartyType } from '../types';

/** The list "Add a person" writes to, made on first use. */
export const MY_PEOPLE_LIST_NAME = 'My people';

/** Shape check for an email the owner types. The same pattern the rules use
 *  for a plan's partyContacts. */
export const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+[.][^@\s]+$/;

export interface ListedPerson {
  listId: string;
  person: Person;
}

/** Lists shown for the area under the map center: the ones saved for that
 *  area, plus every list saved for "everywhere". */
export function listsForArea(lists: PeopleList[], areaId: string): PeopleList[] {
  return lists.filter((l) => l.areaIds.includes('*') || l.areaIds.includes(areaId));
}

/** Where a list is saved, as the id stored in areaIds. Everywhere, or an
 *  unmapped spot, saves for '*' so the list can never end up unreachable. */
export function areaIdsForScope(scope: 'here' | 'everywhere', areaId: string): string[] {
  return scope === 'everywhere' || areaId === 'unassigned' ? ['*'] : [areaId];
}

/** The owner's "My people" list for an area, if "Add a person" has made one. */
export function findMyPeopleList(lists: PeopleList[], areaId: string): PeopleList | null {
  return listsForArea(lists, areaId).find((l) => l.name === MY_PEOPLE_LIST_NAME) ?? null;
}

/** HOA -> 'hoa', Forest Service -> 'national', everything else -> 'other'. */
export function categoryToPartyType(category: string | undefined): PlanPartyType {
  const c = (category ?? '').trim().toLowerCase();
  if (c === 'hoa') return 'hoa';
  if (c === 'forest service') return 'national';
  return 'other';
}

function includes(value: string | undefined, q: string): boolean {
  return (value ?? '').toLowerCase().includes(q);
}

/** Search box: name, organization, title or type, anywhere in the string. */
export function filterListedPeople(people: ListedPerson[], search: string): ListedPerson[] {
  const q = search.trim().toLowerCase();
  if (!q) return people;
  return people.filter(
    ({ person: p }) =>
      includes(p.name, q) ||
      includes(p.organization, q) ||
      includes(p.title, q) ||
      includes(p.category, q)
  );
}

export function flattenLists(
  lists: PeopleList[],
  peopleByList: Record<string, Person[]>
): ListedPerson[] {
  const out: ListedPerson[] = [];
  for (const list of lists) {
    for (const person of peopleByList[list.id] ?? []) out.push({ listId: list.id, person });
  }
  out.sort((a, b) =>
    a.person.name.localeCompare(b.person.name, undefined, { sensitivity: 'base' })
  );
  return out;
}

export function splitByTeam(people: ListedPerson[]): {
  team: ListedPerson[];
  informed: ListedPerson[];
} {
  const team: ListedPerson[] = [];
  const informed: ListedPerson[] = [];
  for (const entry of people) (entry.person.team ? team : informed).push(entry);
  return { team, informed };
}

/** "Chair · Pine Ridge HOA" from whatever of the two is there. */
export function personMeta(p: Person): string {
  return [p.title, p.organization].filter(Boolean).join(' · ');
}
